import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Mail, MapPin, Phone, ExternalLink } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
  const [newsletter, setNewsletter] = useState("");
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. Talk soon.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", subject: "General Inquiry", message: "" });
    } catch {
      toast.error("Could not send right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!newsletter) return;
    try {
      await api.post("/newsletter", { email: newsletter });
      toast.success("You're on the list.");
      setNewsletter("");
    } catch {
      toast.error("Subscription failed. Try again.");
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-charcoal text-warmwhite" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-widest-custom text-gold mb-6">07 — Contact</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight" data-testid="contact-title">
              {`Let's make something `}<span className="italic text-gold">that lasts.</span>
            </h2>
            <p className="mt-8 text-warmwhite/70 leading-relaxed max-w-md">
              {`Whether it's a record, a brand campaign, a destination wedding, or a creative project you can't quite name yet — start here.`}
            </p>

            <ul className="mt-12 space-y-5 text-sm">
              <li className="flex items-center gap-4"><Mail className="w-5 h-5 text-gold" strokeWidth={1.2} /> <a href="mailto:hello@jamesone.co" className="hover:text-gold transition-colors" data-testid="contact-email-link">hello@jamesone.co</a></li>
              <li className="flex items-center gap-4"><Phone className="w-5 h-5 text-gold" strokeWidth={1.2} /> <a href="https://wa.me/13522325546?text=Hi%20James%20%E2%80%94%20I%20came%20from%20your%20site." target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="contact-whatsapp-link">352.232.5546 · WhatsApp</a></li>
              <li className="flex items-center gap-4"><MapPin className="w-5 h-5 text-gold" strokeWidth={1.2} /> Florida · Worldwide</li>
              <li className="flex items-center gap-4"><ExternalLink className="w-5 h-5 text-gold" strokeWidth={1.2} /> <a href="https://itsjamesone.cruisebrothers.com/cb/" target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="contact-travel-link">Plan travel with me →</a></li>
            </ul>

            <div className="mt-16 pt-10 border-t border-warmwhite/15">
              <p className="text-[11px] uppercase tracking-widest-custom text-warmwhite/55 mb-5">Newsletter</p>
              <p className="font-serif text-2xl mb-6 max-w-sm">Quiet, considered notes from the studio. No spam.</p>
              <form onSubmit={subscribe} className="flex gap-0 border-b border-warmwhite/40 focus-within:border-gold transition-colors" data-testid="newsletter-form">
                <input
                  type="email"
                  value={newsletter}
                  onChange={(e) => setNewsletter(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-transparent outline-none py-3 placeholder:text-warmwhite/30 text-warmwhite"
                  data-testid="newsletter-email"
                />
                <button type="submit" className="px-5 py-3 text-[11px] uppercase tracking-widest-custom text-gold hover:text-warmwhite transition-colors" data-testid="newsletter-submit">
                  Subscribe →
                </button>
              </form>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6" data-testid="contact-form">
            <DarkField label="First name *" value={form.first_name} onChange={update("first_name")} testid="contact-first-name" required />
            <DarkField label="Last name" value={form.last_name} onChange={update("last_name")} testid="contact-last-name" />
            <DarkField label="Email *" type="email" value={form.email} onChange={update("email")} testid="contact-email" required />
            <DarkField label="Phone" value={form.phone} onChange={update("phone")} testid="contact-phone" />
            <div className="sm:col-span-2 flex flex-col gap-2">
              <label className="text-[10px] uppercase tracking-widest-custom text-warmwhite/55">Subject</label>
              <select value={form.subject} onChange={update("subject")} className="bg-transparent border-b border-warmwhite/40 focus:border-gold outline-none py-2 font-serif text-lg text-warmwhite [&>option]:text-charcoal" data-testid="contact-subject">
                {["General Inquiry", "Music Production", "Photography", "Travel Planning", "Project / Brand", "Speaking", "Press"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 flex flex-col gap-2">
              <label className="text-[10px] uppercase tracking-widest-custom text-warmwhite/55">Message *</label>
              <textarea rows={5} value={form.message} onChange={update("message")} required className="bg-transparent border-b border-warmwhite/40 focus:border-gold outline-none py-2 font-serif text-lg resize-none text-warmwhite" data-testid="contact-message" />
            </div>
            <div className="sm:col-span-2 flex justify-end mt-4">
              <button type="submit" disabled={busy} className="bg-gold text-charcoal px-10 py-5 text-[11px] uppercase tracking-widest-custom hover:bg-warmwhite transition-colors duration-300 disabled:opacity-60" data-testid="contact-submit">
                {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function DarkField({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[10px] uppercase tracking-widest-custom text-warmwhite/55">{label}</label>
      <input type={type} value={value} onChange={onChange} required={required} className="bg-transparent border-b border-warmwhite/40 focus:border-gold outline-none py-2 font-serif text-lg text-warmwhite" data-testid={testid} />
    </div>
  );
}
